import { UseQueryOptions, useQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";
import {
  AccountTicketDetails,
  BestTicketBlockDetails,
  EventAvailabilityDetails,
  GroupDetails,
  PlanPackageComponentDetails,
  PremiumInventoryDetails,
  ReservationUpdateCartDetails,
  SectionDetails,
  TicketCartDetails,
  TicketDetails,
  TransferDetails,
} from "features/types";
import { get, patch, post } from "services/http";
import { Pageable } from "./types";

export interface ReserveSeatsParams {
  accountId: number;
  planPackageId: number;
  eventId: number;
  section: string;
  row: string;
}

export interface ReserveParams extends ReserveSeatsParams {
  userIds: number[];
}

export interface RedeemParams extends ReserveSeatsParams {
  partySize: number;
}

export interface CalculateChargeItem {
  planPackageId: number | string;
  planPackageComponentId: number | string;
  ticketId: number | string;
}

export interface CalculateGroupChargeItem {
  planPackageId: number | string;
  planPackageComponentId: number | string;
}

export interface ReserveOrRedeemItem {
  planPackageComponentId: number | string;
  userId?: number | string | null;
}

export interface GroupReserveOrRedeemItem {
  planPackageComponentId: number;
  userId?: number;
}

interface Selection {
  section: string;
  row: string;
  seat: string;
}
export interface HoldTicketRequest {
  planPackageId: number;
  eventId: number;
  partySize: number;
  premiumOnly: boolean;
  selections: Selection[];
}

export interface HoldForTflEventRequest {
  eventId: number;
  planPackageId: number;
  quantity: number;
  ticketListingId: string;
  section: string;
  row: string;
}

export type FetchSectionsRequest = {
  eventId?: number;
  planPackageId?: number;
  partySize: number;
};

export type FetchPackageExchangeSectionsRequest = {
  eventId?: number;
  planPackageId?: number;
  partySize: number;
  tdcPackageId?: number | null;
  tdcPackageListId?: number | null;
  tdcBuyerTypeId?: number | null;
};

export type FetchBestBlocksRequest = {
  eventId?: number;
  planPackageId?: number;
  partySize: number;
  sections?: string[];
  premiumOnly: boolean;
};

export type FetchPackageExchangeBlocksRequest = {
  eventId?: number;
  planPackageId?: number;
  partySize: number;
  sections?: string[];
  premiumOnly: boolean;
  tdcPackageId?: number | null;
  tdcPackageListId?: number | null;
  tdcBuyerTypeId?: number | null;
};

export interface CalculateTicketChargeItem {
  planPackageId: number;
  planPackageComponentId: number;
  ticketId: number;
}
interface CalculateReservationUpdateChargesRequest {
  accountId: number;
  eventId: number;
  newTicketItems: CalculateTicketChargeItem[];
  ticketRefundIds: number[];
}

interface HoldTicketsForReservationUpdateRequest {
  accountId: number;
  planPackageId: number;
  eventId: number;
  selections: Selection[];
  premiumOnly: boolean;
  groupCode: string;
}

interface ReserveTicketsForReservationUpdateRequest {
  accountId: number;
  planPackageId: number;
  eventId: number;
  groupCode: string;
  items: ReserveOrRedeemItem[];
  releaseTicketIds: number[];
}

export interface SearchEventParams {
  name?: string;
  startDate?: string;
  endDate?: string;
  venue?: string;
  performer?: string;
  categoryId?: number;
  category?: string;
  postalCode?: string;
  page?: number;
  size?: number;
  city?: string;
  regionCode?: string;
}

export interface EventSearchDetails {
  eventId: string;
  localDateTime: string;
  title: string;
  venueName: string;
  venueCity: string;
  venueState: string;
  categoryName: string;
  thumbnail: string;
  performerName: string;
}

export interface PerformerSearchDetails {
  id: string;
  name: string;
  isHome: boolean;
  isPrimary: boolean;
  thumbnail: string;
}

export interface VenueSearchDetails {
  id: string;
  name: string;
  city: string;
  region: string;
  postalCode: string;
  country: string;
  thumbnail: string;
}

export interface TflAutoCompleteResult {
  events: EventSearchDetails[];
  performers: PerformerSearchDetails[];
  venues: VenueSearchDetails[];
}

export const fetchSectionsRequest = (params: FetchSectionsRequest) => {
  return get({
    url: "/tickets/sections",
    params,
  }) as Promise<SectionDetails[]>;
};

export const fetchPackageExchangeSectionsRequest = (
  params: FetchPackageExchangeSectionsRequest
) => {
  return get({
    url: "/tickets/package_exchange/sections",
    params,
  }) as Promise<SectionDetails[]>;
};

export const fetchBestBlocksRequest = (params: FetchBestBlocksRequest) => {
  return get({
    url: "/tickets/best_blocks",
    params,
  }) as Promise<BestTicketBlockDetails[]>;
};

export const fetchPackageExchangeBestBlocksRequest = (
  params: FetchPackageExchangeBlocksRequest
) => {
  return get({
    url: "/tickets/package_exchange/best_blocks",
    params,
  }) as Promise<BestTicketBlockDetails[]>;
};

type SearchEventParamsKeys = keyof SearchEventParams;

export const useFetchTenantHasTflProviderQuery = () => {
  return useQuery(
    ["fetchTenantHasTflProvider"],
    () => {
      return get<{ value: boolean }>({
        url: "/tickets/tfl",
      }).then((response) => response.value);
    },
    {
      staleTime: Infinity,
    }
  );
};

export const searchEventsRequest = (params: SearchEventParams) => {
  const filteredParams: any = {};
  Object.keys(params).forEach((key) => {
    if (params[key as SearchEventParamsKeys] !== undefined) {
      filteredParams[key] = params[key as SearchEventParamsKeys];
    }
  });
  return get({
    url: "/tickets/tfl/search",
    params: filteredParams,
  }) as Promise<Pageable<EventSearchDetails>>;
};

export const useAutoCompleteResultQuery = (query: string) => {
  return useQuery(
    ["autoCompleteResult", query],
    () => getAutoCompleteResultRequest(query),
    {
      enabled: Boolean(query),
    }
  );
};

export const getAutoCompleteResultRequest = (query: string) => {
  return get({
    url: "/tickets/tfl/auto_complete",
    params: {
      query,
    },
  }) as Promise<TflAutoCompleteResult>;
};

export const useFetchConsumerKeyQuery = () => {
  return useQuery(
    ["fetchConsumerKey"],
    () => get<{ value: string }>({ url: "/tickets/tfl/consumer_key" }),
    {
      refetchInterval: 1000 * 60 * 5, // Refetch every 5 minutes, maybe user has gone stale for more than 5 minutes, doing this will refresh the consumer key
      refetchIntervalInBackground: true,
    }
  );
};

export const holdForTflEventRequest = (params: HoldForTflEventRequest) => {
  return patch({
    url: "/tickets/tfl/hold",
    params,
  }) as Promise<TicketDetails[]>;
};

export const getTflEventAvailabilityRequest = (eventId: string) => {
  return get({
    url: `/tickets/events/tfl/${eventId}`,
  }) as Promise<EventAvailabilityDetails>;
};

export const useFetchTflTicketListingsForEventQuery = (
  eventId: string,
  planPackageId: number
) => {
  return useQuery(
    ["fetchTflTicketListingsForEvent", eventId, planPackageId],
    () => {
      return get({
        url: `/tickets/tfl/${eventId}/tickets`,
        params: {
          planPackageId,
        },
      }) as Promise<{ event: any; listings: any[] }>;
    },
    {
      enabled: Boolean(eventId) && Boolean(planPackageId),
    }
  );
};

export const useFetchExchangeEventAvailablitiesQuery = (params: {
  planPackageId: number;
  ticketIds: number[];
}) => {
  return useQuery(
    ["fetchExchangeEventAvailablities"],
    () => {
      return get({
        url: "/tickets/events/exchange",
        params,
      }) as Promise<Array<EventAvailabilityDetails>>;
    },
    {
      enabled: false,
    }
  );
};

export const useFetchEventAvailablitiesQuery = ({
  accountId,
  planPackageId,
}: {
  accountId: number | undefined;
  planPackageId: number | undefined;
}) => {
  return useQuery(
    ["fetchEventAvailablities", planPackageId],
    () => {
      return get({
        url: "/tickets/events",
        params: {
          accountId,
          planPackageId,
        },
      }) as Promise<Array<EventAvailabilityDetails>>;
    },
    {
      enabled: false,
    }
  );
};

export const useFetchInventoryTypesQuery = (params: {
  planPackageId?: number;
  eventId?: number;
}) => {
  return useQuery(
    ["fetchInventoryTypes", params.eventId, params.planPackageId],
    () => {
      return get({
        url: "/tickets/inventory_types",
        params,
      }) as Promise<{ containsStandard: boolean; containsPremium: boolean }>;
    },
    {
      enabled: Boolean(params.eventId) && Boolean(params.planPackageId),
    }
  );
};

export const useFetchPremiumInventoryQuery = (params: {
  planPackageId?: number;
  eventId?: number;
}) => {
  return useQuery(
    ["fetchPremiumInventory", params.eventId, params.planPackageId],
    () => {
      return get({
        url: "/tickets/premium",
        params,
      }) as Promise<PremiumInventoryDetails[]>;
    },
    {
      enabled: Boolean(params.planPackageId) && Boolean(params.eventId),
    }
  );
};

export const holdTicketsRequest = (params: HoldTicketRequest) =>
  patch({
    url: "/tickets/hold",
    params,
  }) as Promise<TicketDetails[]>;

export const reserveTicketsFetcher = (params: {
  accountId: number;
  planPackageId: number;
  eventId: number;
  section: string;
  row: string;
  items: ReserveOrRedeemItem[];
}) => {
  return patch({
    url: "/tickets/reserve",
    params,
  }) as Promise<TicketDetails[]>;
};

export const groupReserveTicketsFetcher = (params: {
  accountId: number;
  planPackageId: number;
  eventId: number | string;
  groupCode: string;
  items: GroupReserveOrRedeemItem[];
}) => {
  return patch({
    url: "/tickets/group_reserve",
    params,
  });
};

export const releaseTicketFetcher = (ticketIds: number[]) => {
  return patch({
    url: "/tickets/release",
    params: {
      ticketIds,
    },
  });
};

export const initiateTransferFetcher = (params: {
  accountId: number;
  groupCodeId: number;
  planPackageComponentType: PlanPackageComponentDetails["type"];
  quantity: number;
}) => {
  return patch({
    url: "/tickets/initiate_transfer",
    params,
  }) as Promise<TransferDetails>;
};

export const useGetTransferDetailsQuery = (params: {
  eventCode: string;
  transferCode: string;
}) => {
  return useQuery<TransferDetails, AxiosError["response"]>(
    ["getTransferDetails"],
    () =>
      get({
        url: "/tickets/transfer",
        params,
      })
  );
};

export const transferTicketFetcher = (params: {
  eventCode: string;
  transferCode: string;
  quantity: number;
}) => {
  return patch({
    url: "/tickets/transfer",
    params,
  });
};

// https://stackoverflow.com/questions/43082934/how-to-execute-promises-sequentially-passing-the-parameters-from-an-array
export const fetchTicketBarcodeByIdsFetcher = (tickets: TicketDetails[]) => {
  return tickets.reduce(
    (promises, ticket) =>
      promises.then((results) =>
        get({
          url: `/tickets/${ticket.id}/barcode`,
        }).then((r: any) => results.concat(r))
      ),
    Promise.resolve([])
  );
};

export const scanTicketsRequest = (ticketIds: number[]) => {
  return post({
    url: "/tickets/scan",
    params: {
      ticketIds,
    },
  });
};

export const useFetchTicketHistoryQuery = () => {
  return useQuery(
    ["fetchTicketHistory"],
    () =>
      get({
        url: "/tickets/account/ticket_history",
      }) as Promise<AccountTicketDetails[]>
  );
};

export const calculateChargeRequest = (params: {
  accountId: number;
  eventId: string | number;
  items: CalculateChargeItem[];
}) =>
  post({
    url: "/tickets/calculate_charge",
    params,
  }) as Promise<TicketCartDetails>;

export const useCalculateGroupChargeQuery = (params: {
  accountId: number | undefined;
  eventId: string | number;
  groupCode: string;
  items: CalculateGroupChargeItem[];
}) => {
  return useQuery(
    ["fetchGroupCharge"],
    () =>
      post({
        url: `/tickets/calculate_group_charge`,
        params,
      }) as Promise<TicketCartDetails>,
    {
      enabled: false,
    }
  );
};

export const useFetchGroupDetailsQuery = (
  params: {
    eventCode: string;
    groupCode: string;
  },
  options?: UseQueryOptions<GroupDetails, unknown, GroupDetails, string[]>
) => {
  return useQuery(
    ["fetchGroupDetails"],
    () =>
      get({
        url: "/tickets/group_code",
        params,
      }) as Promise<GroupDetails>,
    options
  );
};

export const useCalculateRefundQuery = (params: {
  accountId: number;
  ticketIds: number[];
}) => {
  return useQuery(
    ["calculate_refund", params.ticketIds],
    () =>
      post({
        url: "/tickets/calculate_refund",
        params,
      }) as Promise<TicketCartDetails>
  );
};

export const calculateReservationUpdateChargesRequest = (
  params: CalculateReservationUpdateChargesRequest
) =>
  post({
    url: "/tickets/calculate_reservation_update_charges",
    params,
  }) as Promise<ReservationUpdateCartDetails>;

export const holdForUpdateReservationRequest = (
  params: HoldTicketsForReservationUpdateRequest
) =>
  patch({
    url: "/tickets/hold_for_reservation_update",
    params,
  }) as Promise<TicketDetails[]>;

export const reserveForReservationUpdateRequest = (
  params: ReserveTicketsForReservationUpdateRequest
) =>
  patch({
    url: "/tickets/reserve_for_reservation_update",
    params,
  }) as Promise<TicketDetails[]>;
